import useDialogue from "@ch/helpers/hooks/useDialogue";
import localforage from "localforage";
import moment from "moment";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import Checkbox from "../Inputs/Checkbox";
import Modal from "../Modals/Modal";

interface StoredModalState {
  count: number;
  lastUpdated: string;
  optedOut: boolean;
}

const MAX_DISMISSALS = 3;
const STORAGE_KEY = "adsModalState";

const getTodayKey = () => moment().format("YYYY-MM-DD");

const createDefaultState = () => ({
  count: 0,
  lastUpdated: getTodayKey(),
  optedOut: false,
});

const Bait: FC<{ baitRef: React.RefObject<HTMLDivElement | null> }> = ({
  baitRef,
}) => {
  return (
    <div
      aria-hidden
      className="adsbox ad ads banner adsbygoogle ad-placement ad-slot doubleclick text-[0px]"
      ref={baitRef}
      style={{
        height: 1,
        width: 1,
        position: "absolute",
        left: -9999,
        top: -9999,
        pointerEvents: "none",
      }}
    />
  );
};

const DISABLE_KEY = "__DISABLE_ADBLOCK_MODAL__";

const AdsBlockerDetection: FC = () => {
  const shouldBypassDetection =
    import.meta.env.VITE_DISABLE_ADBLOCK_DETECTION === "1" ||
    (typeof window !== "undefined" &&
      ((window as unknown as Record<string, unknown>)[DISABLE_KEY] as
        | boolean
        | undefined) === true) ||
    (typeof navigator !== "undefined" && navigator.webdriver === true);

  const [isDismissed, setIsDismissed] = useState(false);
  const [detected, setDetected] = useState(false);
  const [modalState, setModalState] =
    useState<StoredModalState>(createDefaultState);
  const [isCountLoaded, setIsCountLoaded] = useState(false);
  const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);
  const baitRef = useRef<HTMLDivElement | null>(null);

  const {
    dialogueId,
    handleOpenDialogue,
    handleCloseDialogue,
    handleUnmountDialogue,
    isDialogueOpen,
  } = useDialogue();

  const isBaitHidden = useMemo(() => {
    return () => {
      const el = baitRef.current;
      if (!el) return false;
      const style = window.getComputedStyle(el);
      const hiddenBySize =
        el.offsetParent === null ||
        el.offsetHeight === 0 ||
        el.offsetWidth === 0;
      const hiddenByStyle =
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.opacity === "0";
      return hiddenBySize || hiddenByStyle;
    };
  }, []);

  useEffect(() => {
    if (shouldBypassDetection) return undefined;

    let cancelled = false;
    void (async () => {
      const todayKey = getTodayKey();
      let nextState = createDefaultState();
      try {
        const stored = await localforage.getItem<StoredModalState>(STORAGE_KEY);
        if (stored?.lastUpdated === todayKey) {
          nextState = stored;
        } else {
          await localforage.setItem(STORAGE_KEY, nextState);
        }
      } finally {
        if (!cancelled) {
          setModalState(nextState);
          setIsCountLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldBypassDetection]);

  // signal 1: cosmetic filtering via bait
  useEffect(() => {
    if (shouldBypassDetection) return undefined;

    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      if (!cancelled && isBaitHidden()) {
        setDetected(true);
      }
    });
    const t = setTimeout(() => {
      if (!cancelled && isBaitHidden()) {
        setDetected(true);
      }
    }, 400);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [isBaitHidden, shouldBypassDetection]);

  // signal 2: MutationObserver for late cosmetic changes or removal
  useEffect(() => {
    if (shouldBypassDetection) return undefined;

    const el = baitRef.current;
    if (!el) return undefined;

    let detectedOnce = false;
    const observer = new MutationObserver(() => {
      if (!detectedOnce && isBaitHidden()) {
        detectedOnce = true;
        setDetected(true);
      }
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    let parentObserver: MutationObserver | undefined;
    if (el.parentElement) {
      parentObserver = new MutationObserver(() => {
        if (!document.body.contains(el)) {
          setDetected(true);
        }
      });
      parentObserver.observe(el.parentElement, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
      parentObserver?.disconnect();
    };
  }, [isBaitHidden, shouldBypassDetection]);

  // signal 3: network probe to a commonly blocked ads script
  useEffect(() => {
    if (shouldBypassDetection) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    const t = setTimeout(() => {
      void (async () => {
        try {
          await fetch(
            "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js",
            {
              method: "HEAD",
              mode: "no-cors",
              signal: controller.signal,
              credentials: "omit",
            },
          );
          // In some cases no-cors resolves silently even if blocked; rely on catch when thrown
        } catch {
          if (!cancelled) setDetected(true);
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(t);
    };
  }, [shouldBypassDetection]);

  // defer opening to avoid calling flushSync error during renders in the useDialogue hook
  const didOpenRef = useRef(false);
  useEffect(() => {
    if (shouldBypassDetection) return;
    if (
      !isCountLoaded ||
      modalState.optedOut ||
      !detected ||
      isDismissed ||
      didOpenRef.current
    ) {
      return;
    }
    didOpenRef.current = true;
    setDontShowAgainChecked(false);
    queueMicrotask(() => {
      handleOpenDialogue();
    });
  }, [
    detected,
    handleOpenDialogue,
    isCountLoaded,
    isDismissed,
    modalState.optedOut,
    shouldBypassDetection,
  ]);

  const handleDismiss = () => {
    setIsDismissed(true);
    handleCloseDialogue();
    setModalState((prev) => {
      const todayKey = getTodayKey();
      const base = prev.lastUpdated === todayKey ? prev : createDefaultState();
      const hasCheckbox = base.count >= MAX_DISMISSALS;
      let nextState: StoredModalState;
      if (hasCheckbox && dontShowAgainChecked) {
        nextState = {
          ...base,
          optedOut: true,
          lastUpdated: todayKey,
          count: Math.max(base.count, MAX_DISMISSALS),
        };
      } else {
        const nextCount = Math.min(base.count + 1, MAX_DISMISSALS);
        nextState = {
          ...base,
          count: nextCount,
          lastUpdated: todayKey,
          optedOut: false,
        };
      }

      void localforage.setItem(STORAGE_KEY, nextState);
      return nextState;
    });
  };

  const hasReachedCheckboxThreshold = modalState.count >= MAX_DISMISSALS;
  const shouldShowCheckbox =
    isCountLoaded && !modalState.optedOut && hasReachedCheckboxThreshold;

  if (shouldBypassDetection) {
    return null;
  }

  return (
    <>
      <Bait baitRef={baitRef} />
      <Modal
        cancelButtonLabel="Dismiss"
        handleCancel={handleDismiss}
        isModalOpen={isDialogueOpen}
        modalBoxClassName="!h-auto max-w-lg"
        modalId={dialogueId}
        onDismiss={handleUnmountDialogue}
        showCloseIcon
      >
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Ad blocker detected</h2>
          <p className="text-sm text-gray-600">
            We noticed an ad blocker is enabled. While we don’t show ads,
            blockers can sometimes interfere with parts of this site. For
            maximum usage of features, please disable the ad blocker for this
            site.
          </p>
          {shouldShowCheckbox && (
            <Checkbox
              checked={dontShowAgainChecked}
              containerClassName="text-sm text-gray-600 gap-2"
              label="Don’t show again"
              onChange={(event) => {
                setDontShowAgainChecked(event.target.checked);
              }}
              wrapperClassName="mt-1"
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default AdsBlockerDetection;
